import { useState, Suspense, useEffect } from "react";
import { RENTER_STATUS } from "../../../../../types/renter.type";
import GenericTable from "../../../../../common/components/ui/table/GenericTable";
import { useNavigate } from "react-router-dom";
import Loading from "../../../../../common/components/ui/loading/Loading";
import {
  TableFiltersState,
  TableFilters,
} from "../../../../../common/components/ui/table/TableFilters";
import TableFooter from "../../../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../../../common/components/ui/toast/toast.context";
import { ICommission } from "../../../../../types/commission.type";
import { formatDateAndTime } from "../../../../../common/helpers/date.helpers";
import { getAllActivations } from "@/common/services/commission.api";

interface CommissionSummaryItem {
  type: string;
  count: number;
  totalValue: number;
}

const STATUS_LABELS = {
  [RENTER_STATUS.Ativo]: "Ativo",
  [RENTER_STATUS.Incompleto]: "Incompleto",
  [RENTER_STATUS.PendentePagamento]: "Pendente de pagamento",
  [RENTER_STATUS.ProntoParaAprovacao]: "Pronto para aprovação",
  [RENTER_STATUS.ReservaPaga]: "Reserva paga",
  [RENTER_STATUS.ReservaPendentePagamento]: "Reserva pendente de pagamento",
  [RENTER_STATUS.Finished]: "Finalizado",
  [RENTER_STATUS.Recision]: "Recisão",
  [RENTER_STATUS.Recall]: "Recolhimento",
  [RENTER_STATUS.WaitingRescision]: "Aguardando recisão",
  [RENTER_STATUS.Congelado]: "Congelado",
};

const TYPE_COMMISSION_LABELS: Record<string, string> = {
  // R$ 20
  "activation-sv": "Ativação SV",
  "activation-plus": "Ativação Plus",
  "renew-plus": "Renovação Plus",
  plus: "Upgrade Plus",
  "migration-sv": "Migração SV",
  "migration-plus": "Migração Plus",
  // R$ 50
  "activation-plus-sv": "Ativação Plus+SV",
  "migration-plus-sv": "Migração Plus+SV",
  // Adicione todos os outros tipos aqui conforme a sua enum COMMISSION_TYPE
};

type CommissionProps = {
  commission: ICommission[];
};

function Activation(props: CommissionProps) {
  const commission = props.commission;
  const headers = {
    renterName: "Nome",
    licensePlate: "Placa",
    activationDate: "Ativado em",
    typeCommission: "Bonificação",
    valueComission: "Valor Bonificação",
  };
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [commissionActivation, setCommission] = useState<ICommission[]>([]);
  const [commissionSummary, setCommissionSummary] = useState<
    CommissionSummaryItem[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-activationDate",
  });
  const [consultantTotal, setConsultantTotal] = useState(0);
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });

  const technicalConsultant = commission[0].technicalConsultant;
  useEffect(() => {
    async function loadComissionActivation(filters, page) {
      setIsLoading(true);
      try {
        const result = await getAllActivations({
          page,
          ...filters,
          technicalConsultant,
        });
        if (!result.ok) {
          notifyError("Erro ao carregar ativações.");
          return;
        }

        setCommission(result.data);
        setData({ itemsPerPage, totalItems: result.totalItems });
        setConsultantTotal(result.consultantTotal);
        setCommissionSummary(result.commissionSummary || []);
      } catch (err) {
        notifyError("Erro inesperado ao carregar ativações.");
      } finally {
        setIsLoading(false);
      }
    }

    if (technicalConsultant !== "") {
      loadComissionActivation(filters, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicalConsultant, page, filters.search, filters]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const renderCommissionSummary = (summary: CommissionSummaryItem[]) => {
    if (!summary || summary.length === 0)
      return (
        <p className="text-gray-500 text-sm italic">
          Nenhuma bonificação encontrada no período.
        </p>
      );

    return (
      <div className="flex flex-col gap-1 mt-1 text-s text-gray-700">
        {summary.map((item) => (
          <div
            key={item.type}
            className="flex justify-start items-center text-gray-700"
          >
            <span className="font-normal">
              {TYPE_COMMISSION_LABELS[item.type] || item.type}:
            </span>
            <span className="font-semibold text-right ml-2">
              {item.count} un.
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="tabs-border-line-commission">
        <div className="tabs-item-style-commission">
          <TableFilters
            dashboardHeader="Ativações"
            placeholder="nome"
            statusOptions={STATUS_LABELS}
            newItemLabel="Novo Cliente"
            onSearch={handleFilterChange}
            entity="commission"
            tableData={commissionActivation}
          />
          <h2 className="font-semibold">{`Resumo das Ativações - ${technicalConsultant}`}</h2>
          <div className="flex gap-4 mb-4">
            <div className="bg-white shadow p-4 rounded-xl w-full border border-gray-200">
              <p className="text-sm font-medium text-gray-600">
                Bonificação Consultor (Total)
              </p>
              <p className="text-xl font-bold text-green-600 mb-2">
                R$ {consultantTotal.toFixed(2)}
              </p>
              <hr className="mb-2" />
              <p className="text-sm font-medium text-gray-600">
                Detalhe por Tipo:
              </p>
              {renderCommissionSummary(commissionSummary)}
            </div>
          </div>

          <GenericTable
            headers={headers}
            data={commissionActivation}
            setFilters={({ orderBy }) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                orderBy,
              }));
            }}
            renders={
              {
                renterName: (name, commission) => (
                  <a
                    href={`/clientes/${commission.renterId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/clientes/${commission.renterId}`);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {name as string}
                  </a>
                ),
                licensePlate: (licensePlate, comission) => (
                  <a
                    href={`/veiculos/${(comission as ICommission).licensePlate
                      }`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        `/veiculos/${(comission as ICommission).licensePlate}`
                      );
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {licensePlate as string}
                  </a>
                ),
                activationDate: formatDateAndTime,
                currentContractStartDate: formatDateAndTime,
                typeCommission: (type: string) =>
                  TYPE_COMMISSION_LABELS[type] || type,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
            entity="commissions"
          />

          {isLoading && (
            <div className="fixed top-0 left-0 right-0 bottom-0 z-[9999] bg-black/30 backdrop-blur-sm flex items-center justify-center">
              <Loading />
            </div>
          )}

          <TableFooter
            page={page || 1}
            totalItems={data.totalItems || 0}
            itemsPerPage={+(filters.itemsPerPage ?? 10)}
            goToPage={setPage}
          />
        </div>
      </div>
    </Suspense>
  );
}

export default Activation;
